import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { alertsApi, rolesApi, authApi, ticketsApi, claimEmailsApi } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  ChartLineUp,
  Users,
  FileText,
  Buildings,
  SignOut,
  List,
  X,
  UserCircle,
  Gear,
  Money,
  Monitor,
  CaretDown,
  ShieldCheck,
  Warning,
  CurrencyCircleDollar,
  CreditCard,
  ChartBar,
  Bell,
  MapPin,
  Hash,
  TreeStructure,
  ClockCounterClockwise,
  Gauge,
  Crown,
  Key,
  Fingerprint,
  Ticket,
  Phone,
  Envelope,
  Brain,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Toaster, toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_566bb76b-f932-4e23-952a-9935b75c7780/artifacts/i6y9m6q5_53561592_transparent11%20-%20Copy.png";

const navItems = [
  { path: '/admin-dashboard', icon: Gauge, label: 'Platform Dashboard', module: 'admin_dashboard', color: 'text-violet-400' },
  { path: '/tenants', icon: Buildings, label: 'Hospitals', module: 'tenants', color: 'text-cyan-400' },
  { path: '/plans', icon: Crown, label: 'Plans & Pricing', module: 'plans', color: 'text-amber-400' },
  { path: '/renewals', icon: ClockCounterClockwise, label: 'Renewals', module: 'renewals', color: 'text-rose-400' },
  { path: '/security', icon: ShieldCheck, label: 'Security', module: 'security', color: 'text-emerald-400' },
  { path: '/policies', icon: FileText, label: 'Policies', module: 'policies', color: 'text-violet-400' },
  { path: '/abdm-admin', icon: Fingerprint, label: 'ABDM / NHCX', module: 'abdm_admin', color: 'text-teal-400' },
  { path: '/tickets-admin', icon: Ticket, label: 'Ticket Management', module: 'tickets_admin', color: 'text-indigo-400' },
  { path: '/platform-users', icon: UserCircle, label: 'Platform Team', module: 'platform_users', color: 'text-pink-400' },
  { path: '/dashboard', icon: ChartLineUp, label: 'Dashboard', module: 'dashboard', color: 'text-sky-400' },
  { path: '/executive-dashboard', icon: ChartBar, label: 'Executive Dashboard', module: 'executive_dashboard', color: 'text-indigo-400' },
  { path: '/ai-analytics', icon: Brain, label: 'AI Analytics', module: 'ai_analytics', color: 'text-violet-400' },
  { path: '/approvals', icon: ShieldCheck, label: 'Approvals', module: 'approvals', color: 'text-amber-400' },
  { path: '/tpa-portal', icon: Buildings, label: 'TPA Portal', module: 'tpa_portal', color: 'text-teal-400' },
  { path: '/claims', icon: FileText, label: 'Cases', module: 'claims', color: 'text-violet-400' },
  { path: '/preauths', icon: ShieldCheck, label: 'Pre-Auth', module: 'preauths', color: 'text-blue-400' },
  { path: '/denials', icon: Warning, label: 'Denials', module: 'denials', color: 'text-rose-400' },
  { path: '/ar-followup', icon: CurrencyCircleDollar, label: 'AR & Collections', module: 'ar_followup', color: 'text-amber-400' },
  { path: '/patients', icon: Users, label: 'Patients', module: 'patients', color: 'text-emerald-400' },
  { path: '/payers', icon: Money, label: 'Payers/TPA', module: 'payers', color: 'text-orange-400' },
  { path: '/display-board', icon: Monitor, label: 'Status Board', module: 'display_board', color: 'text-teal-400' },
  { path: '/abdm', icon: Fingerprint, label: 'ABDM Integration', module: 'abdm', color: 'text-teal-400' },
  { path: '/tickets', icon: Ticket, label: 'Tickets', module: 'tickets', color: 'text-indigo-400' },
  { path: '/email-center', icon: Envelope, label: 'Email Center', module: 'email_center', color: 'text-violet-400' },
  { path: '/payments', icon: CreditCard, label: 'Payments', module: 'payments', color: 'text-green-400' },
  { path: '/reports', icon: ChartBar, label: 'Reports', module: 'reports', color: 'text-indigo-400' },
  { path: '/notifications', icon: Bell, label: 'Notifications', module: 'notifications', color: 'text-yellow-400' },
  { path: '/alerts', icon: Warning, label: 'Alerts', module: 'alerts', color: 'text-red-400' },
  { path: '/workflow', icon: TreeStructure, label: 'Workflow', module: 'workflow', color: 'text-fuchsia-400' },
  { path: '/branches', icon: MapPin, label: 'Branches', module: 'branches', color: 'text-teal-400' },
  { path: '/numbering-config', icon: Hash, label: 'Numbering', module: 'numbering', color: 'text-orange-400' },
  { path: '/hospital-users', icon: UserCircle, label: 'Team Members', module: 'hospital_users', color: 'text-pink-400' },
  { path: '/subscription', icon: Crown, label: 'Subscription', module: 'subscription', color: 'text-amber-400' },
  { path: '/users', icon: UserCircle, label: 'Users', module: 'users', color: 'text-pink-400' },
  { path: '/audit-logs', icon: ClockCounterClockwise, label: 'Audit Logs', module: 'audit_logs', color: 'text-slate-400' },
  { path: '/settings', icon: Gear, label: 'Settings', module: 'settings', color: 'text-slate-400' },
];

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [ticketCount, setTicketCount] = useState(0);
  const [emailUnread, setEmailUnread] = useState(0);
  const [allowedPaths, setAllowedPaths] = useState([]);
  const [pwdDialog, setPwdDialog] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState(true);
  const [twoFaAutoAt, setTwoFaAutoAt] = useState(null);
  const [twoFaAutoDays, setTwoFaAutoDays] = useState(null);
  const [twoFaDialog, setTwoFaDialog] = useState(false);
  const [selectedDays, setSelectedDays] = useState(10);

  useEffect(() => {
    const load2faStatus = async () => {
      try {
        const res = await authApi.get2faStatus();
        setTwoFaEnabled(res.data?.two_fa_enabled ?? true);
        setTwoFaAutoAt(res.data?.auto_enable_at || null);
        setTwoFaAutoDays(res.data?.auto_enable_days || null);
      } catch {}
    };
    if (user) load2faStatus();
  }, [user]);

  useEffect(() => {
    const loadAlertCount = async () => {
      try {
        const res = await alertsApi.count();
        setAlertCount(res.data?.count || 0);
      } catch {}
    };
    loadAlertCount();
    const iv = setInterval(loadAlertCount, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const loadTicketCount = async () => {
      try {
        const isPlatform = user?.role === 'super_admin' || user?.roles?.some(r => ['super_admin','platform_admin','support_team'].includes(r));
        if (isPlatform) {
          const res = await ticketsApi.adminOverview();
          const p = res.data?.platform_totals || {};
          setTicketCount((p.open || 0) + (p.escalated || 0));
        } else {
          const res = await ticketsApi.dashboard();
          setTicketCount((res.data?.open || 0) + (res.data?.assigned || 0) + (res.data?.escalated || 0));
        }
      } catch {}
    };
    if (user) loadTicketCount();
    const iv = setInterval(() => { if (user) loadTicketCount(); }, 60000);
    return () => clearInterval(iv);
  }, [user]);

  useEffect(() => {
    const loadEmailUnread = async () => {
      try {
        const isPlatform = user?.role === 'super_admin' || user?.roles?.some(r => ['super_admin','platform_admin','support_team'].includes(r));
        if (!isPlatform && user?.tenant_id) {
          const res = await claimEmailsApi.unreadCount();
          setEmailUnread(res.data?.count || 0);
        }
      } catch {}
    };
    if (user) loadEmailUnread();
    const iv = setInterval(() => { if (user) loadEmailUnread(); }, 30000);
    return () => clearInterval(iv);
  }, [user]);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const res = await rolesApi.myPermissions();
        setAllowedPaths(res.data?.paths || []);
      } catch {
        setAllowedPaths(['/dashboard']);
      }
    };
    if (user) loadPermissions();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleToggle2FA = async () => {
    if (twoFaEnabled) {
      // Turning OFF — show dialog to pick auto-re-enable days
      setSelectedDays(10);
      setTwoFaDialog(true);
    } else {
      // Turning ON manually
      try {
        await authApi.toggle2fa({ enabled: true });
        setTwoFaEnabled(true);
        setTwoFaAutoAt(null);
        setTwoFaAutoDays(null);
        toast.success('Two-Factor Authentication enabled');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to update 2FA');
      }
    }
  };

  const handleDisable2FA = async () => {
    try {
      const res = await authApi.toggle2fa({ enabled: false, auto_enable_days: selectedDays });
      setTwoFaEnabled(false);
      setTwoFaAutoAt(res.data?.auto_enable_at || null);
      setTwoFaAutoDays(res.data?.auto_enable_days || null);
      setTwoFaDialog(false);
      toast.success(`2FA disabled for ${selectedDays} days — will auto re-enable`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update 2FA');
    }
  };

  const handleChangePassword = async () => {
    if (!pwdForm.current_password || !pwdForm.new_password) {
      toast.error('All fields are required');
      return;
    }
    if (pwdForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setPwdLoading(true);
    try {
      await authApi.changePassword({
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success('Password changed successfully');
      setPwdDialog(false);
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwdLoading(false);
    }
  };

  const filteredNavItems = navItems.filter(item => allowedPaths.includes(item.path));

  const getRoleBadge = (role, roles) => {
    const roleLabels = {
      super_admin: 'Super Admin',
      hospital_admin: 'Hospital Admin',
      claim_manager: 'Claim Manager',
      billing_executive: 'Billing Executive',
      tpa_desk: 'TPA Desk',
      ar_executive: 'AR Executive',
      finance_manager: 'Finance Manager',
      management_viewer: 'Management Viewer',
      reception_user: 'Reception User',
    };
    const userRoles = roles && roles.length > 0 ? roles : (role ? [role] : []);
    return userRoles.map(r => roleLabels[r] || r).join(', ');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-layout">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="px-5 py-4 border-b border-slate-700/50">
            <img src={LOGO_URL} alt="Care'O Logo" className="h-10 brightness-0 invert" />
            <p className="text-[10px] text-slate-500 mt-1 tracking-wider">Powered by Steward Healthcare</p>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-sky-500/20 to-violet-500/10 text-white border-l-2 border-sky-400'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <item.icon className={`h-5 w-5 ${item.color}`} weight="regular" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-slate-700/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-sky-500 to-violet-500 flex items-center justify-center text-white font-semibold text-sm shadow-lg shadow-sky-500/20">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400">{getRoleBadge(user?.role, user?.roles)}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start gap-2 border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white hover:border-slate-600 bg-transparent"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <SignOut className="h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-button"
            >
              {sidebarOpen ? <X className="h-6 w-6 text-slate-700" /> : <List className="h-6 w-6 text-slate-700" />}
            </button>

            <div className="flex-1 lg:flex-none"></div>

            <div className="flex items-center gap-2">
              {/* Inbox / Email Center */}
              <Button variant="ghost" className="relative hover:bg-violet-50 gap-1.5" data-testid="header-email-btn"
                onClick={() => navigate('/email-center')}>
                <Envelope className="h-5 w-5 text-violet-500" weight="duotone" />
                <span className="hidden md:inline text-xs font-semibold text-violet-600">Inbox</span>
                {emailUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-violet-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {emailUnread > 99 ? '99+' : emailUnread}
                  </span>
                )}
              </Button>

              {/* Raise Ticket / Tickets */}
              <Button variant="ghost" className="relative hover:bg-indigo-50 gap-1.5" data-testid="header-ticket-btn"
                onClick={() => {
                  const isPlatform = user?.role === 'super_admin' || user?.roles?.some(r => ['super_admin','platform_admin','support_team'].includes(r));
                  navigate(isPlatform ? '/tickets-admin' : '/tickets/new');
                }}>
                <Ticket className="h-5 w-5 text-indigo-500" weight="duotone" />
                <span className="hidden md:inline text-xs font-semibold text-indigo-600">Raise Ticket</span>
                {ticketCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {ticketCount > 99 ? '99+' : ticketCount}
                  </span>
                )}
              </Button>

              <Button variant="ghost" className="relative hover:bg-slate-100" onClick={() => navigate('/alerts')} data-testid="header-alerts-btn">
                <Bell className="h-5 w-5 text-slate-500" />
                {alertCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {alertCount > 99 ? '99+' : alertCount}
                  </span>
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 hover:bg-slate-100" data-testid="user-menu-button">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-sky-500 to-violet-500 flex items-center justify-center text-white text-sm font-semibold">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                    <span className="hidden md:inline text-slate-700 font-medium">{user?.name}</span>
                    <CaretDown className="h-4 w-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 border-b border-slate-100 mb-1">
                    <p className="text-xs text-slate-500">{getRoleBadge(user?.role, user?.roles)}</p>
                  </div>
                  <DropdownMenuItem className="flex items-center gap-2" onClick={() => { setPwdDialog(true); setPwdForm({ current_password: '', new_password: '', confirm_password: '' }); }}>
                    <Key className="h-4 w-4" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuItem className="flex items-center justify-between" onClick={handleToggle2FA} data-testid="toggle-2fa-btn">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4" />
                      <span>2FA Login</span>
                    </div>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${twoFaEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {twoFaEnabled ? 'ON' : twoFaAutoDays ? `OFF (${twoFaAutoDays}d)` : 'OFF'}
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-600">
                    <SignOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8">
          {/* Plan Expired Banner */}
          {user?.plan_expired && user?.role !== 'super_admin' && (
            <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 flex items-center gap-3" data-testid="plan-expired-banner">
              <Warning className="h-6 w-6 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-800 text-sm">Subscription Plan Expired</p>
                <p className="text-xs text-amber-600">You can view and download existing data, but creating new entries is disabled. Please upgrade your plan to continue.</p>
              </div>
              <a href="/subscription" className="px-4 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm rounded-md font-medium hover:from-amber-600 hover:to-orange-600 whitespace-nowrap">
                Upgrade Plan
              </a>
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={pwdDialog} onOpenChange={setPwdDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-['Outfit']">Change Password</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Current Password</Label>
              <Input type="password" value={pwdForm.current_password}
                onChange={e => setPwdForm(f => ({...f, current_password: e.target.value}))} data-testid="current-password-input" />
            </div>
            <div>
              <Label>New Password</Label>
              <Input type="password" placeholder="Min 6 characters" value={pwdForm.new_password}
                onChange={e => setPwdForm(f => ({...f, new_password: e.target.value}))} data-testid="new-password-input" />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input type="password" value={pwdForm.confirm_password}
                onChange={e => setPwdForm(f => ({...f, confirm_password: e.target.value}))} data-testid="confirm-password-input" />
            </div>
            <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white"
              onClick={handleChangePassword} disabled={pwdLoading} data-testid="change-password-btn">
              {pwdLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <Dialog open={twoFaDialog} onOpenChange={setTwoFaDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-500" /> Disable 2FA Temporarily
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">
              Select how many days you want to disable Two-Factor Authentication. It will <strong>automatically re-enable</strong> after the selected period.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[10, 20, 30].map(d => (
                <button key={d} onClick={() => setSelectedDays(d)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${selectedDays === d
                    ? 'border-amber-400 bg-amber-50 text-amber-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
                  data-testid={`2fa-days-${d}`}>
                  <p className="text-xl font-bold">{d}</p>
                  <p className="text-[10px]">days</p>
                </button>
              ))}
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-700">
                2FA will be <strong>automatically re-enabled</strong> on{' '}
                <strong>{new Date(Date.now() + selectedDays * 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
              </p>
            </div>
            <Button onClick={handleDisable2FA}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-2"
              data-testid="confirm-disable-2fa-btn">
              <ShieldCheck className="h-4 w-4" /> Disable 2FA for {selectedDays} Days
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default DashboardLayout;
